/**
 * What a lift car means right now, for guests and for robots: the two
 * lines at the top of the lift drawer (twin slice 1, W6 step 11).
 *
 * The words are `liftReading()`'s, verbatim, so the drawer says exactly
 * what the attention card and the building view say about the same car.
 * This component writes no lift text of its own.
 *
 * The ops layer adds what the hotel's words leave out, read from data:
 * the robot map's routes through a car robots are not meant to use
 * (the FL1/FL2 line, present only while the asset carries their lanes),
 * and the caveat when the lift adapter reports no freshness of its own.
 * A site audience (FOH, `site_supervisor`, `service_operator`) gets the
 * two lines only.
 */
import type { Audience } from '../../auth/roles';
import type { LiftColumnState } from './lift-column-state';
import './LiftReadingLines.css';

export interface LiftReadingLinesProps {
  readonly column: Pick<LiftColumnState, 'reading' | 'caveat' | 'look'>;
  readonly audience: Audience;
}

export function LiftReadingLines({ column, audience }: LiftReadingLinesProps) {
  const { reading, caveat, look } = column;
  const ops = audience === 'ops';
  // Dimmed while the reading is not live (quiet, checking, off), as the
  // car is drawn: the words may be right, but they are not current.
  const className = look === 'dim' ? 'nx-lift-reading nx-lift-reading--dim' : 'nx-lift-reading';
  return (
    <section
      className={className}
      aria-label="What this lift means now"
      data-testid="lift-reading-lines"
    >
      <div className="nx-lift-reading__line">
        <span className="nx-lift-reading__who">Guests</span>
        <span data-testid="lift-reading-guests">{reading.guests}</span>
      </div>
      <div className="nx-lift-reading__line">
        <span className="nx-lift-reading__who">Robots</span>
        <span data-testid="lift-reading-robots">{reading.robots}</span>
      </div>
      {ops && reading.opsNote && (
        <p className="nx-lift-reading__ops" data-testid="lift-reading-ops-note">
          {reading.opsNote}
        </p>
      )}
      {ops && caveat && (
        <p className="nx-lift-reading__ops" data-testid="lift-reading-caveat">
          {caveat}
        </p>
      )}
    </section>
  );
}
